import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  broadcastNotification,
  createNotificationTemplate,
  deleteNotificationTemplate,
  getNotificationSettings,
  listNotificationTemplates,
  listNotifications,
  markNotificationRead,
  sendNotification,
  updateNotificationSettings,
  updateNotificationTemplate,
  type Notification,
  type NotificationBroadcastCreate,
  type NotificationCreate,
  type NotificationSettingsUpdate,
  type NotificationTemplateCreate,
  type NotificationTemplateUpdate,
} from './api';

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationsKeys.all, 'list'] as const,
  settings: () => [...notificationsKeys.all, 'settings'] as const,
  templates: (activeOnly = false) =>
    [...notificationsKeys.all, 'templates', activeOnly ? 'active' : 'all'] as const,
};

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: listNotifications,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationsKeys.list() });
      const previous = qc.getQueryData<Notification[]>(notificationsKeys.list());
      const readAt = new Date().toISOString();
      qc.setQueryData<Notification[]>(notificationsKeys.list(), (rows) =>
        (rows ?? []).map((row) =>
          row.id === id ? { ...row, is_read: true, read_at: row.read_at ?? readAt } : row,
        ),
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(notificationsKeys.list(), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.list() });
    },
  });
}

export function useNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: notificationsKeys.settings(),
    queryFn: getNotificationSettings,
    enabled,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettingsUpdate) => updateNotificationSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.settings() });
      qc.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}

export function useNotificationTemplates(activeOnly = false, enabled = true) {
  return useQuery({
    queryKey: notificationsKeys.templates(activeOnly),
    queryFn: () => listNotificationTemplates(activeOnly),
    enabled,
  });
}

export function useCreateNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationTemplateCreate) => createNotificationTemplate(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useUpdateNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: NotificationTemplateUpdate }) =>
      updateNotificationTemplate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useDeleteNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteNotificationTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useSendNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationCreate) => sendNotification(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useBroadcastNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationBroadcastCreate) => broadcastNotification(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}
